import {
  PRIMARY_RESIDENCE, SECOND_LOAN_SUB,
  AUTO, CHILDCARE, HOME_EQUITY, INSTALLMENT, LEASE, LIENS, MORTGAGE,
  THIRTY_DAY_CHARGE_ACCT, OTHER_LIABILITIES,
  REVOLVING_CHARGE, SEPARATE_MAINTENANCE, STUDENT_LOAN, ALIMONY
} from "../../constants/liability_types";
import {
  INCLUDE_IN_DTI, OMITTED, WILL_BE_PAID_OFF
} from "../../constants/account_dispositions";
import { default_debt_info_account } from "../../schema/debt_info";
import { ACCOUNT_ID_PREFIX } from "../../constants/debt-info";
import { deep_clone_simple } from "../../constants/utils";
import generate_id from "../../constants/id-generator";
import { mismoBool } from "../mismo_util";
import { attr } from "../relationships";

/**
 * MISMO LiabilityType -> LANIS liability type.
 *
 * The export side collapses several LANIS types onto one MISMO type (Auto,
 * Boat, Motorcycle and RV all go out as "Installment"), so this mapping cannot
 * be a perfect inverse — "Installment" has to come back as the generic
 * Installment Loan. The creditor name is preserved either way, so the LO can
 * re-pick the specific type if they care about the distinction.
 */
const LANIS_LIABILITY_TYPE = {
  Alimony: ALIMONY,
  ChildSupport: CHILDCARE,
  CollectionsJudgementsAndLiens: LIENS,
  HELOC: HOME_EQUITY,
  Installment: INSTALLMENT,
  LeasePayments: LEASE,
  MortgageLoan: MORTGAGE,
  Open30DayChargeAccount: THIRTY_DAY_CHARGE_ACCT,
  Other: OTHER_LIABILITIES,
  Revolving: REVOLVING_CHARGE,
  SeparateMaintenance: SEPARATE_MAINTENANCE,
  StudentLoan: STUDENT_LOAN,
  TaxLien: LIENS,
  Taxes: LIENS,
  // Seen from POINT / other origination systems.
  HomeEquityLineOfCredit: HOME_EQUITY,
  FirstMortgage: PRIMARY_RESIDENCE,
  SecondMortgage: SECOND_LOAN_SUB,
  AutomobileLoan: AUTO
};

/**
 * Import one <LIABILITY> into document.accountSummaries.
 *
 * Ownership comes from the RELATIONSHIPS block (LIABILITY_IsAssociatedWith_ROLE),
 * pre-parsed into context.relationships.liabilityOwners. Without that arc there
 * is nothing on the LIABILITY element itself that says whose debt it is, so we
 * fall back to Borrower 1.
 */
export function importLiability(obj, context) {
  const { document, liabilityRecord } = context;
  if (!liabilityRecord) return;

  const {
    LiabilityAccountIdentifier: accountNumber,
    LiabilityExclusionIndicator: excluded,
    LiabilityMonthlyPaymentAmount: monthlyPayment,
    LiabilityPayoffStatusIndicator: paidOff,
    LiabilityType: liabilityType,
    LiabilityUnpaidBalanceAmount: unpaidBalance,
    LiabilityHighCreditAmount: highCredit,
    HELOCMaximumBalanceAmount: helocMax
  } = obj;

  const account = liabilityRecord.account;

  if (accountNumber) {
    account.account_number = String(accountNumber);
  }

  if (liabilityType && LANIS_LIABILITY_TYPE[liabilityType]) {
    account.liability_type = LANIS_LIABILITY_TYPE[liabilityType];
  } else {
    if (liabilityType) {
      console.warn("Unmapped MISMO LiabilityType:", liabilityType);
    }
    account.liability_type = OTHER_LIABILITIES;
  }

  if (monthlyPayment !== undefined) {
    const parsed = parseFloat(monthlyPayment);
    account.monthly_payment = Number.isFinite(parsed)
      ? parsed.toFixed(2)
      : "0.00";
  }

  if (unpaidBalance !== undefined) {
    const parsed = parseFloat(unpaidBalance);
    if (Number.isFinite(parsed)) {
      account.current_balance = parsed.toFixed(2);
    }
  }

  // High Credit / Credit Limit -> drives "Percent of Credit Used". Standard DU
  // files put it in LiabilityHighCreditAmount; some credit vendors (PCBD /
  // MeridianLink) instead carry the revolving account's limit in
  // HELOCMaximumBalanceAmount. Prefer the explicit high-credit field, then fall
  // back to whichever is actually populated (> 0).
  const highCreditValue = [highCredit, helocMax]
    .map(v => (v === undefined ? NaN : parseFloat(v)))
    .find(n => Number.isFinite(n) && n > 0);
  if (highCreditValue !== undefined) {
    account.high_credit_limit = highCreditValue.toFixed(2);
  }

  // How this debt is treated on the loan: omitted, paid off at closing, or
  // counted in the DTI.
  let disposition = INCLUDE_IN_DTI;
  if (mismoBool(excluded)) {
    disposition = OMITTED;
  } else if (mismoBool(paidOff)) {
    disposition = WILL_BE_PAID_OFF;
  }

  liabilityRecord.disposition = disposition;

  // Apply the disposition to every loan so the debt behaves consistently
  // regardless of which loan the LO has selected.
  document.loans.forEach(loan => {
    if (!loan.account_dispositions) {
      loan.account_dispositions = {};
    }
    loan.account_dispositions[account.id] = disposition;
  });
}

/** The creditor name lives in a separate child element. */
export function importLiabilityHolder(obj, { liabilityRecord }) {
  if (!liabilityRecord) return;
  if (obj && obj.NAME && obj.NAME.FullName) {
    liabilityRecord.account.description = String(obj.NAME.FullName);
  }
}

/** Identity key for deduping: account # + creditor. */
function liabilityKey(obj) {
  const acct =
    obj.LIABILITY_DETAIL && obj.LIABILITY_DETAIL.LiabilityAccountIdentifier;
  const creditor =
    obj.LIABILITY_HOLDER && obj.LIABILITY_HOLDER.NAME && obj.LIABILITY_HOLDER.NAME.FullName;
  // No account number → don't risk merging two different debts from the same
  // creditor; fall back to the element label so it stays unique.
  if (!acct) return null;
  return `${String(acct).trim()}|${String(creditor || "").trim().toLowerCase()}`;
}

/**
 * Create-or-merge the LANIS debt record for a <LIABILITY>.
 *
 * The same debt reaches us two different ways: a real DU file writes it ONCE
 * with an arc to each owner, while POINT writes it as a separate element per
 * borrower (one arc each). Both must end up as a single row whose `owners`
 * lists every borrower. We dedup on account # + creditor and union the owners.
 *
 * Returns the context patch { liabilityRecord } the child populators write into.
 */
export function upsertLiabilityRecord(obj, context, arcOwners) {
  const { document } = context;
  if (!context.liabilityByKey) context.liabilityByKey = {};
  if (!context.liabilityLabels) context.liabilityLabels = {};

  const owners = (arcOwners && arcOwners.length > 0 ? arcOwners : [0]).map(String);
  const key = liabilityKey(obj);
  const label = attr(obj, "xlink:label");

  // Already seen this account → merge owners into the existing row.
  if (key && context.liabilityByKey[key]) {
    const existing = context.liabilityByKey[key];
    const merged = existing.account.owners ? [...existing.account.owners] : [];
    owners.forEach((o) => {
      if (!merged.includes(o)) merged.push(o);
    });
    merged.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    existing.account.owners = merged;
    existing.account.borrower = merged[0];
    if (label) context.liabilityLabels[label] = existing.account;
    return { liabilityRecord: existing };
  }

  const account = deep_clone_simple(default_debt_info_account);
  const nextIndex = Object.keys(document.accountSummaries).length;
  account.id = generate_id(ACCOUNT_ID_PREFIX, true, nextIndex);
  account.owners = [...owners];
  account.borrower = owners[0];

  document.accountSummaries[account.id] = account;

  const record = { account, disposition: INCLUDE_IN_DTI };
  if (key) context.liabilityByKey[key] = record;
  if (label) context.liabilityLabels[label] = account;
  return { liabilityRecord: record };
}
